import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "@/lib/router-compat";

const CANONICAL_DOMAIN = "https://systematlas.fr";
const DEFAULT_TITLE = "System Atlas: AI study assistant for students";
const DEFAULT_DESCRIPTION = "Upload your courses and get structured revision sheets, auto-generated quizzes, and spaced repetition in seconds. The AI study assistant for students.";
const DEFAULT_IMAGE = "https://storage.googleapis.com/gpt-engineer-file-uploads/fixHLaYpiGQriTbTH3NKQDMP9X13/social-images/social-1776280806353-Screenshot_2026-04-15_at_21.19.52.webp";
const SUPPORTED_ROUTE_LOCALES = ["fr", "es", "de", "it", "zh", "hi", "pt", "pl"] as const;
const OG_LOCALES: Record<string, string> = {
  en: "en_US", fr: "fr_FR", es: "es_ES", de: "de_DE", it: "it_IT",
  zh: "zh_CN", hi: "hi_IN", pt: "pt_PT", pl: "pl_PL",
};

interface SEOConfig {
  title?: string;
  description?: string;
  image?: string;
  type?: "website" | "article";
  noIndex?: boolean;
  customPath?: string;
  alternates?: { hreflang: string; href: string }[];
}

/**
 * Comprehensive SEO hook for managing page-level metadata.
 * Handles: title, description, canonical URL, Open Graph, Twitter Cards.
 */
export const useSEO = (config: SEOConfig = {}) => {
  const location = useLocation();
  const { i18n } = useTranslation();

  useEffect(() => {
    const {
      title = DEFAULT_TITLE,
      description = DEFAULT_DESCRIPTION,
      image = DEFAULT_IMAGE,
      type = "website",
      noIndex = false,
      customPath,
    } = config;

    const path = customPath ?? location.pathname;
    const cleanPath = path === "/" ? "" : path.replace(/\/$/, "");
    const canonicalUrl = `${CANONICAL_DOMAIN}${cleanPath}`;
    const routeLocale = location.pathname.match(/^\/([a-z]{2})(?:\/|$)/)?.[1];
    const documentLocale = routeLocale && SUPPORTED_ROUTE_LOCALES.includes(routeLocale as typeof SUPPORTED_ROUTE_LOCALES[number])
      ? routeLocale
      : i18n.language?.split("-")[0] ?? "en";

    document.title = title;
    document.documentElement.lang = documentLocale;

    const setMeta = (name: string, content: string, property = false) => {
      const attr = property ? "property" : "name";
      let element = document.querySelector<HTMLMetaElement>(`meta[${attr}="${name}"]`);
      if (!element) {
        element = document.createElement("meta");
        element.setAttribute(attr, name);
        document.head.appendChild(element);
      }
      element.content = content;
    };

    const setLink = (rel: string, href: string) => {
      let element = document.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
      if (!element) {
        element = document.createElement("link");
        element.rel = rel;
        document.head.appendChild(element);
      }
      element.href = href;
    };

    setMeta("description", description);
    setLink("canonical", canonicalUrl);

    if (noIndex) {
      setMeta("robots", "noindex, nofollow");
    } else {
      setMeta("robots", "index, follow");
    }

    setMeta("og:title", title, true);
    setMeta("og:description", description, true);
    setMeta("og:url", canonicalUrl, true);
    setMeta("og:type", type, true);
    setMeta("og:image", image, true);
    setMeta("og:site_name", "System Atlas", true);
    setMeta("og:locale", OG_LOCALES[documentLocale] ?? "en_US", true);

    setMeta("twitter:card", "summary_large_image");
    setMeta("twitter:title", title);
    setMeta("twitter:description", description);
    setMeta("twitter:image", image);
    setMeta("twitter:site", "@SystemAtlas");

    // Only remove alternates this hook created. Links rendered by the route
    // head() belong to React; removing them crashes the next navigation.
    const removeOwnAlternates = () =>
      document.querySelectorAll<HTMLLinkElement>('link[hreflang][data-use-seo]').forEach(el => el.remove());
    removeOwnAlternates();
    const hasRouteAlternates = document.querySelector('link[hreflang]:not([data-use-seo])') !== null;

    if (!hasRouteAlternates && config.alternates && config.alternates.length > 0) {
      const uniqueAlternates = new Map(config.alternates.map(alternate => [alternate.hreflang.toLowerCase(), alternate]));
      uniqueAlternates.forEach(({ hreflang, href }) => {
        const link = document.createElement("link");
        link.rel = "alternate";
        link.hreflang = hreflang;
        link.setAttribute("data-use-seo", "");
        link.href = new URL(href, CANONICAL_DOMAIN).href.replace(/\/$/, "");
        document.head.appendChild(link);
      });
    }

    return removeOwnAlternates;
  }, [location.pathname, i18n.language, config.title, config.description, config.image, config.type, config.noIndex, config.customPath, config.alternates]);
};

/**
 * Pre-defined SEO configurations for common pages
 */
export const SEO_CONFIGS = {
  home: {
    title: "System Atlas - AI Study Assistant & Revision Sheets",
    description: "Upload your courses and get structured revision sheets, auto-generated quizzes, and spaced repetition in seconds. The AI study assistant for students.",
  },
  signIn: {
    title: "Sign In | System Atlas - AI Study Assistant",
    description: "Log in to System Atlas. Access your revision sheets, quizzes, and study plan. Continue studying smarter with AI.",
  },
  mySheets: {
    title: "My Revision Sheets | System Atlas",
    description: "Access and manage your AI-generated revision sheets. Organize by folder, track mastery scores, and follow your spaced repetition schedule.",
    noIndex: true,
  },
  newContent: {
    title: "Create Revision Sheet | System Atlas",
    description: "Transform any course material into a structured revision sheet. Upload PDFs, paste text, or enter YouTube URLs for AI-powered study materials.",
    noIndex: true,
  },
  settings: {
    title: "Settings | System Atlas",
    description: "Manage your System Atlas account, security preferences, and subscription settings.",
    noIndex: true,
  },
  notFound: {
    title: "Page Not Found | System Atlas",
    description: "The page you're looking for doesn't exist. Return to System Atlas to continue studying.",
    noIndex: true,
  },
  pricing: {
    title: "Pricing - Free & Pro Plans | System Atlas",
    description: "Compare System Atlas plans. Free: 2 sheets/day with quizzes. Pro: unlimited sheets, gap analysis, defense mode. Start with a 7-day free trial.",
  },
  studyPlan: {
    title: "Study Plan | System Atlas",
    description: "Your personalized study plan powered by SM-2 spaced repetition. See due reviews, track progress, and optimize your revision schedule.",
    noIndex: true,
  },
  learnHub: {
    title: "Study Guides & Learning Techniques | System Atlas",
    description: "Free evidence-based study guides on active recall, spaced repetition, the Feynman technique, and AI-powered revision methods.",
  },
  learnHubFr: {
    title: "Guides d'apprentissage et techniques de revision | System Atlas",
    description: "Guides gratuits sur le rappel actif, la repetition espacee, la technique Feynman et la revision assistee par IA. Methodes prouvees pour mieux reviser.",
  },
  templateSheets: {
    title: "Revision Sheet Templates | System Atlas",
    description: "Browse free revision sheet templates for medicine, law, engineering, sciences and more. Structured summaries ready to study from.",
  },
} as const;